/**
 * 查找/替换的纯文本匹配逻辑（与编辑器无关，便于单测）。
 *
 * 为什么需要它：编辑器此前把 `Ctrl+F` 劫持成"聚焦全局搜索"（只读检索），
 * 却没有提供任何查找替换能力 —— 等于覆盖了浏览器原生 Ctrl+F 又没给替代品。
 * 改人名、改术语这类操作只能手工逐处修改。
 */

/**
 * ProseMirror 会把块之间用单个 `\n` 连接（见 `editor.getText()`）。
 * 本模块所有偏移量都基于这份文本，替换时再映射回文档位置。
 */
export const BLOCK_SEPARATOR = '\n';

export interface MatchOptions {
  /** 区分大小写 */
  caseSensitive?: boolean;
  /** 按正则解释查询串 */
  regex?: boolean;
}

export interface TextMatch {
  /** 匹配起始偏移（基于 getText() 的文本） */
  start: number;
  /** 匹配长度 */
  length: number;
}

export interface MatchResult {
  matches: TextMatch[];
  /** 正则非法时的错误提示（此时 matches 为空） */
  error?: string;
}

/** 转义正则元字符，使用户输入的普通文本按字面量匹配 */
export function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 构造匹配用的正则；查询串为空返回 null */
export function buildMatchRegExp(query: string, options?: MatchOptions): RegExp | null {
  if (!query) return null;
  const source = options?.regex ? query : escapeRegExp(query);
  // 全局匹配需要 g 标志；这里统一加，调用方通过 exec 循环或 matchAll 使用
  return new RegExp(source, options?.caseSensitive ? 'g' : 'gi');
}

/**
 * 在文本中查找全部匹配。返回的 `matches` 不含重叠匹配。
 */
export function findMatches(text: string, query: string, options?: MatchOptions): MatchResult {
  const re = (() => {
    try {
      return buildMatchRegExp(query, options);
    } catch (e) {
      return e as Error;
    }
  })();

  if (re === null) return { matches: [] };
  if (re instanceof Error) return { matches: [], error: '正则表达式无效' };

  const matches: TextMatch[] = [];
  // 零宽匹配（如 `^*`）会导致 exec 死循环，显式跳过
  for (const m of text.matchAll(re)) {
    const length = m[0].length;
    if (length === 0) continue;
    matches.push({ start: m.index ?? 0, length });
    if (matches.length > 20_000) break; // 防御性上限
  }
  return { matches };
}

/**
 * 计算替换后的文本与替换次数。
 * `all=false` 时只替换第一处（"替换"按钮），`all=true` 时全部替换。
 * 替换串支持 `$1` 反向引用（正则模式）。
 */
export function applyReplacement(
  text: string,
  query: string,
  replacement: string,
  options?: MatchOptions & { all?: boolean },
): { text: string; count: number } {
  const { matches, error } = findMatches(text, query, options);
  if (error || matches.length === 0) return { text, count: 0 };

  const selected = options?.all === false ? matches.slice(0, 1) : matches;
  const limit = selected.length;

  // 借助 RegExp 自身的替换语义处理 $1 / $& 等反向引用：
  // 逐个命中替换，用回调计数，达到 limit 后原样保留其余内容。
  let done = 0;
  const re = buildMatchRegExp(query, options);
  if (!re) return { text, count: 0 };
  const out = text.replace(re, (...args) => {
    if (done >= limit) return args[0] as string;
    done++;
    // 把 match 之外的参数交给一个"单次替换"用的小正则，避免重复实现 $ 展开
    return expandReplacement(replacement, args);
  });
  return { text: out, count: done };
}

/**
 * 展开替换串中的 `$&`、`$1..$9`、`$$`。
 * 直接实现而非再跑一次正则，避免零宽匹配与转义带来的边界问题。
 */
function expandReplacement(replacement: string, args: unknown[]): string {
  const whole = String(args[0]);
  // args = [match, p1..pn, offset, string, groups?]
  const groups: Array<string | undefined> = [];
  for (let i = 1; i < args.length; i++) {
    const a = args[i];
    if (typeof a === 'string' || a === undefined) groups.push(a as string | undefined);
    else break; // offset 起为非分组参数
  }
  return replacement.replace(/\$(\$|&|\d{1,2})/g, (_m, token: string) => {
    if (token === '$') return '$';
    if (token === '&') return whole;
    const idx = Number(token);
    return groups[idx - 1] ?? '';
  });
}
